import { DateTime } from 'luxon';
import { useSelector } from '~Hooks';

// When handling dates in QO we use Date type and not DateTime from luxon.
// Reason for this is that react-calendar uses Date type and not DateTime.
// That is why convert toJSDate() is used.

export const useDates = () => {
  const companyTimezone = useSelector((state) => state.companyTimeZone);

  // Helper function: This function is used to convert the date to the correct timezone
  const getDateWithCorrectTimezone = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const specificDate = DateTime.fromObject(
      { year: year, month: month, day: day, hour: 0, minute: 0 },
      { zone: companyTimezone },
    );

    return specificDate;
  };

  // Helper function: Set time to accounting start time
  const setStartTime = (date: DateTime) => {
    return date.set({
      hour: 5,
      minute: 30,
      second: 0,
      millisecond: 0,
    });
  };

  // Helper function: Set time to accounting end time
  const setEndTime = (date: DateTime) => {
    return date.plus({ day: 1 }).set({
      hour: 5,
      minute: 29,
      second: 59,
      millisecond: 999,
    });
  };

  // Get accounting start date and time - returns a utc string
  const getAccountingStartDate = (date: Date): string => {
    const correctDate = getDateWithCorrectTimezone(date);
    const startDateAndTime = setStartTime(correctDate)
      .toUTC()
      .toISO();

    return startDateAndTime ?? '';
  };

  // Get accounting end date and time - returns a utc string
  const getAccountingEndDate = (date: Date): string => {
    const correctDate = getDateWithCorrectTimezone(date);
    const endDateAndTime = setEndTime(correctDate)
      .toUTC()
      .toISO();

    return endDateAndTime ?? '';
  };

  // Additional methods to get specific dates and periods
  const getStartDate = (date: Date): Date => {
    const correctDate = getDateWithCorrectTimezone(date);
    const startDate = setStartTime(correctDate).toJSDate();
    return startDate;
  };

  const getEndDate = (date: Date): Date => {
    const correctDate = getDateWithCorrectTimezone(date);
    const endDate = setEndTime(correctDate).toJSDate();
    return endDate;
  };

  const getToday = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const today = setStartTime(correctDate).toUTC();
    return today.toJSDate();
  };

  const getTodayString = (): string => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const today = setStartTime(correctDate)
      .toUTC()
      .toString();
    return today;
  };

  const getStartOfCurrentWeek = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfWeek = setStartTime(correctDate.startOf('week'));
    return startOfWeek.toJSDate();
  };

  const getEndOfCurrentWeek = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfWeek = setEndTime(correctDate.endOf('week'));
    return endOfWeek.toJSDate();
  };

  // N(umber) weeks back
  const getStartOfLastNWeeks = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfWeek = setStartTime(correctDate.startOf('week').minus({ weeks: n }));
    return startOfWeek.toJSDate();
  };

  const getEndOfLastNWeeks = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfWeek = setEndTime(correctDate.minus({ weeks: n }).endOf('week'));
    return endOfWeek.toJSDate();
  };

  const getStartOfCurrentMonth = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfMonth = setStartTime(correctDate.startOf('month'));
    return startOfMonth.toJSDate();
  };

  const getEndOfCurrentMonth = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfMonth = setEndTime(correctDate.endOf('month'));
    return endOfMonth.toJSDate();
  };

  // N(umber) months back
  const getStartOfLastNMonths = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfMonth = setStartTime(correctDate.startOf('month').minus({ months: n }));
    return startOfMonth.toJSDate();
  };

  const getEndOfLastNMonths = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfMonth = setEndTime(correctDate.minus({ months: n }).endOf('month'));
    return endOfMonth.toJSDate();
  };

  const getStartOfCurrentQuarter = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfQuarter = setStartTime(correctDate.startOf('quarter'));
    return startOfQuarter.toJSDate();
  };

  const getEndOfCurrentQuarter = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfQuarter = setEndTime(correctDate.endOf('quarter'));
    return endOfQuarter.toJSDate();
  };

  // N(umber) quarters back
  const getStartOfLastNQuarters = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfQuarter = setStartTime(correctDate.startOf('quarter').minus({ quarters: n }));
    return startOfQuarter.toJSDate();
  };

  const getEndOfLastNQuarters = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfQuarter = setEndTime(correctDate.minus({ quarters: n }).endOf('quarter'));
    return endOfQuarter.toJSDate();
  };

  const getStartOfCurrentYear = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfYear = setStartTime(correctDate.startOf('year'));
    return startOfYear.toJSDate();
  };

  const getEndOfCurrentYear = (): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfYear = setEndTime(correctDate.endOf('year'));
    return endOfYear.toJSDate();
  };

  // N(umber) years back
  const getStartOfLastNYears = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const startOfYear = setStartTime(correctDate.startOf('year').minus({ years: n }));
    return startOfYear.toJSDate();
  };

  const getEndOfLastNYears = (n: number): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date());
    const endOfYear = setEndTime(correctDate.minus({ years: n }).endOf('year'));
    return endOfYear.toJSDate();
  };

  const getStartOfLastNDays = (n: number, yearsAgo = 0): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date()).minus({
      years: yearsAgo,
    });
    const startOfPeriod = setStartTime(correctDate.minus({ days: n }));
    return startOfPeriod.toJSDate();
  };

  const getEndOfLastNDays = (yearsAgo = 0): Date => {
    const correctDate = getDateWithCorrectTimezone(new Date()).minus({
      years: yearsAgo,
    });
    const endOfPeriod = setEndTime(correctDate);
    return endOfPeriod.toJSDate();
  };

  const getStartOfAllTime = (): Date => {
    // Assuming '2017-04-01' as the foundation date of Baemingo
    const foundationDateJS = new Date('2017-04-01');
    const foundationDate = getDateWithCorrectTimezone(foundationDateJS);
    return setStartTime(foundationDate).toJSDate();
  };

  const isBetweenMidnightAnd530 = (date: DateTime): boolean => {
    return date.hour >= 0 && (date.hour < 5 || (date.hour === 5 && date.minute < 30));
  };

  return {
    getAccountingStartDate,
    getAccountingEndDate,
    getStartDate,
    getEndDate,
    getToday,
    getTodayString,
    getStartOfCurrentWeek,
    getEndOfCurrentWeek,
    getStartOfLastNWeeks,
    getEndOfLastNWeeks,
    getStartOfCurrentMonth,
    getEndOfCurrentMonth,
    getStartOfLastNMonths,
    getEndOfLastNMonths,
    getStartOfCurrentQuarter,
    getEndOfCurrentQuarter,
    getStartOfLastNQuarters,
    getEndOfLastNQuarters,
    getStartOfCurrentYear,
    getEndOfCurrentYear,
    getStartOfLastNYears,
    getEndOfLastNYears,
    getStartOfLastNDays,
    getEndOfLastNDays,
    getStartOfAllTime,
    isBetweenMidnightAnd530,
    getDateWithCorrectTimezone,
    setStartTime,
  };
};
